/**
 * Turning a drag into a drawing — the decidable half of the drawing tools.
 *
 * A drawing tool is a mouse gesture, and almost none of it needs a mouse. Which
 * coordinates a kind wants, what a stroke looks like once the pointer has
 * finished wandering, whether a gesture produced anything worth keeping: all of
 * that is arithmetic over two points and an array, and all of it is wrong in
 * ways that are invisible on a running chart and obvious in a test.
 *
 * So the surface's job shrinks to what only it can do — capture the pointer and
 * convert pixels to data values — and everything after that happens here.
 *
 * ## A gesture is in data space by the time it arrives
 *
 * The modifier converts through the axis' coordinate calculator before calling
 * in, so this file never sees a pixel. That matters because the X value it
 * receives is a **bar index** and what gets stored is a **timestamp**: the
 * conversion runs at the boundary (`annotation-x.ts`), and a function taking
 * pixels would have to know about both, which is two conversions in one place
 * and the reason drawings drift.
 *
 * ## A click is not a drag
 *
 * Every two-point kind can be started and abandoned by a user who clicked to
 * dismiss something. A zero-size box is invisible, un-selectable and permanent,
 * so `MIN_DRAG` discards it — except for the kinds that are *placed* rather than
 * dragged, where a click is the whole gesture.
 */

import type { Annotation } from "@qufolio/model";

import { needsTwoPoints, type AnnotationKind } from "./annotation.js";

/**
 * What a freshly drawn annotation looks like before anyone restyles it.
 *
 * A **token**, never a hex (D13) — a drawing made under the dark theme has to
 * follow the palette when the user switches, and a stored `#4c8dff` would be the
 * one thing on the chart still wearing the old one.
 *
 * `accent` rather than the renderer's `textSecondary` fallback, because a new
 * drawing has to be *findable*: the fallback is the same grey as the axis labels
 * and a trendline in it reads as chart furniture rather than as the thing that
 * was just drawn.
 */
export const DEFAULT_DRAW_STYLE = { stroke: "accent", thickness: 2 } as const;

/** A highlighted bar's colour until the user picks another (owner: a picker). */
export const BAR_HIGHLIGHT_FILL = "positive";

/** How strong a highlighted bar's column is — the forming band's ~13%. */
export const BAR_HIGHLIGHT_OPACITY = 0.13;

/** A point in data space: X is a bar index, Y is a price. */
export interface DrawPoint {
  x: number;
  y: number;
}

/**
 * How far the pointer must travel for a drag to count, in bar indices.
 *
 * In *index* space rather than pixels because that is what the gesture arrives
 * in, and because the threshold should mean the same thing on a zoomed-in chart
 * as a zoomed-out one — half a bar is a click at every zoom level, where three
 * pixels is a click at one and a deliberate short trendline at another.
 */
export const MIN_DRAG = 0.5;

/**
 * A gesture in progress, as the annotation it would become.
 *
 * Returned for the preview as well as the commit, so what is drawn under the
 * pointer and what gets stored cannot disagree — the live rubber-band is the
 * same function as the final drawing, which is the only way the shape does not
 * jump on release.
 */
export function draftAnnotation(
  kind: AnnotationKind,
  id: string,
  start: DrawPoint,
  current: DrawPoint,
  path?: readonly DrawPoint[],
): Annotation {
  if (kind === "freehand") {
    return {
      id,
      type: kind,
      style: DEFAULT_DRAW_STYLE,
      points: (path ?? [start, current]).map((p) => ({ ...p })),
    };
  }
  if (kind === "hline") {
    // Spans its pane, so the X the user happened to press at is not part of it.
    // Storing one would be a coordinate nothing reads and that a later edit
    // would silently contradict.
    return { id, type: kind, style: DEFAULT_DRAW_STYLE, y1: current.y };
  }
  if (kind === "text") {
    return { id, type: kind, style: DEFAULT_DRAW_STYLE, x1: start.x, y1: start.y, text: "" };
  }
  if (kind === "barHighlight") {
    // One bar and no price; the caller has already snapped `x` to a bar.
    return { id, type: kind, x1: start.x, style: { fill: BAR_HIGHLIGHT_FILL } };
  }
  return {
    id,
    type: kind,
    style: DEFAULT_DRAW_STYLE,
    x1: start.x,
    y1: start.y,
    x2: current.x,
    y2: current.y,
  };
}

/**
 * Whether a finished gesture is worth keeping.
 *
 * The guard against the drawing nobody meant: a click that happened to land on
 * the canvas while a tool was armed leaves a zero-size annotation which cannot
 * be seen and therefore cannot be selected to delete. Un-drawable and permanent
 * is the worst of the options, so the gesture is discarded instead.
 *
 * A one-point kind is exempt, because for those a click *is* the gesture.
 */
export function isWorthKeeping(kind: AnnotationKind, start: DrawPoint, end: DrawPoint): boolean {
  if (kind === "freehand") return Math.abs(end.x - start.x) >= MIN_DRAG;
  if (!needsTwoPoints(kind)) return true;
  return Math.abs(end.x - start.x) >= MIN_DRAG;
}

/**
 * A freehand path with the points nobody would miss removed.
 *
 * A pointer emits an event every few milliseconds, so a two-second stroke is
 * several hundred points describing a line that eight would draw identically.
 * Storing the raw path costs a row per point in the workspace and a line
 * segment per point at render, for a curve that looks the same either way.
 *
 * Ramer–Douglas–Peucker: keep the point furthest from the chord, recurse on
 * both halves, drop everything within `epsilon` of the line it already lies on.
 * It is shape-preserving by construction — the error is bounded by `epsilon`
 * everywhere, so a stroke cannot develop a corner it did not have.
 *
 * The distance is computed in a normalised space rather than in data units,
 * because X is bar indices and Y is dollars: an un-normalised epsilon that
 * simplifies a $400 stock sensibly erases a $4 one entirely.
 */
export function simplifyPath(
  points: readonly DrawPoint[],
  epsilon = 0.002,
): DrawPoint[] {
  if (points.length <= 2) return points.map((p) => ({ ...p }));

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  // A stroke drawn straight across has zero height; dividing by its span would
  // make every point infinitely far from the chord and simplify nothing.
  const spanX = maxX - minX || 1;
  const spanY = maxY - minY || 1;
  const norm = (p: DrawPoint) => ({ x: (p.x - minX) / spanX, y: (p.y - minY) / spanY });

  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;

  // Explicit stack rather than recursion: a stroke can carry thousands of
  // points, and the recursion depth is the path length in the degenerate case.
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [first, last] = stack.pop()!;
    if (last <= first + 1) continue;

    const a = norm(points[first]!);
    const b = norm(points[last]!);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const chord = Math.hypot(dx, dy);

    let worst = 0;
    let worstAt = -1;
    for (let i = first + 1; i < last; i++) {
      const p = norm(points[i]!);
      // Perpendicular distance to the chord, or to the endpoint when the chord
      // has collapsed to a point — a stroke that returns to where it started.
      const d =
        chord === 0
          ? Math.hypot(p.x - a.x, p.y - a.y)
          : Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x) / chord;
      if (d > worst) {
        worst = d;
        worstAt = i;
      }
    }

    if (worst > epsilon && worstAt > 0) {
      keep[worstAt] = true;
      stack.push([first, worstAt], [worstAt, last]);
    }
  }

  return points.filter((_, i) => keep[i]!).map((p) => ({ ...p }));
}

/**
 * The Fibonacci retracement ratios, as the levels a `fib` annotation draws.
 *
 * Defaults rather than a constant the renderer reaches for: `Annotation.levels`
 * is in the contract, so a user who wants 0.786 gets it by storing it, and this
 * is only what a freshly drawn one starts with. 0 and 1 are included because
 * they are the two ends of the drag and a retracement is read against them.
 */
export const FIB_RATIOS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];

/** A `fib` with its default ratios, labelled as percentages. */
export function fibDefaults(): { value: number; label: string }[] {
  return FIB_RATIOS.map((value) => ({ value, label: `${(value * 100).toFixed(1)}%` }));
}
